import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getDashboardStats } from '../lib/api';
import { Package, Warehouse, Store, BookmarkCheck, TrendingUp, TrendingDown, AlertCircle, Settings, Euro, PackageOpen, ToggleLeft, ToggleRight, FileSpreadsheet, Download, Upload, Truck, DollarSign, Receipt, ShoppingCart, Users } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import LoadingModal from '../components/LoadingModal';

// Format price with Romanian locale (comma as decimal separator)
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0';
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2).replace('.', ',');
};

export const Dashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [profitStats, setProfitStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [showPalletModal, setShowPalletModal] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [newPalletNumber, setNewPalletNumber] = useState('');
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);
    
    // Settings
    const [autoCreateEnabled, setAutoCreateEnabled] = useState(true);
    const [euroRate, setEuroRate] = useState(5);
    const [tempEuroRate, setTempEuroRate] = useState('5');

    useEffect(() => {
        loadStats();
        loadSettings();
        loadProfitStats();
    }, []);

    const loadStats = async () => {
        try {
            setLoading(true);
            const response = await getDashboardStats();
            setStats(response.data);
        } catch (err) {
            setError('Nu s-au putut încărca statisticile');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadProfitStats = async () => {
        try {
            const response = await api.get('/stats/profit');
            setProfitStats(response.data);
        } catch (err) {
            console.error('Error loading profit stats:', err);
        }
    };

    const loadSettings = async () => {
        try {
            const response = await api.get('/settings');
            setAutoCreateEnabled(response.data.auto_create_enabled ?? true);
            setEuroRate(response.data.euro_rate ?? 5);
            setTempEuroRate((response.data.euro_rate ?? 5).toString());
        } catch (err) {
            console.error('Error loading settings:', err);
        }
    };

    const saveSettings = async () => {
        const rate = parseFloat(tempEuroRate);
        if (rate <= 0) {
            toast.error('Introduceți o valoare validă pentru curs');
            return;
        }
        
        try {
            await api.put('/settings', {
                auto_create_enabled: autoCreateEnabled,
                euro_rate: rate
            });
            setEuroRate(rate);
            localStorage.setItem('euroRate', rate.toString());
            toast.success('Setări salvate');
            setShowSettings(false);
        } catch (err) {
            toast.error('Eroare la salvarea setărilor');
        }
    };

    const openNewPallet = async () => {
        const num = parseInt(newPalletNumber);
        if (!num || num <= 0) {
            toast.error('Introduceți un număr valid de palet');
            return;
        }
        
        try {
            await api.post('/pallets', { number: num });
            toast.success(`Palet ${num} deschis! Paletul anterior a fost închis.`);
            setShowPalletModal(false);
            setNewPalletNumber('');
            loadStats();
        } catch (err) {
            toast.error('Eroare la deschiderea paletului');
        }
    };

    // Export data
    const handleExportData = () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        window.open(`${backendUrl}/api/data/export-file`, '_blank');
        toast.success('Se descarcă backup-ul datelor...');
    };

    // Import data
    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.json')) {
            toast.error('Te rog selectează un fișier JSON');
            return;
        }

        if (!window.confirm('ATENȚIE: Aceasta va înlocui TOATE datele existente cu cele din fișier. Continui?')) {
            e.target.value = '';
            return;
        }

        setImporting(true);
        try {
            const text = await file.text();
            const data = JSON.parse(text);
            
            console.log('Importing data:', Object.keys(data));
            
            const response = await api.post('/data/import', data);
            
            toast.success(`Date importate! ${Object.entries(response.data.imported).map(([k,v]) => `${k}: ${v}`).join(', ')}`);
            loadStats();
        } catch (err) {
            console.error('Import error:', err);
            console.error('Error response:', err.response?.data);
            toast.error(err.response?.data?.detail || 'Eroare la importarea datelor');
        } finally {
            setImporting(false);
            e.target.value = '';
        }
    };

    const activePallet = stats?.active_pallet;
    const palletDate = activePallet?.opened_at 
        ? new Date(activePallet.opened_at).toLocaleDateString('ro-RO')
        : null;

    const statCards = stats ? [
        {
            label: 'Depozit',
            value: stats.total_depozit_quantity,
            valueLabel: `${formatPrice(stats.depozit_value)} RON`,
            icon: Warehouse,
            color: 'text-amber-600',
            bg: 'bg-amber-50',
            link: '/depozit',
        },
        {
            label: 'Magazin',
            value: stats.total_store_quantity,
            valueLabel: `${formatPrice(stats.store_value)} RON`,
            icon: Store,
            color: 'text-emerald-600',
            bg: 'bg-emerald-50',
            link: '/store',
        },
    ] : [];

    return (
        <div data-testid="dashboard-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Dashboard
                    </h1>
                    <p className="text-zinc-500 mt-1">Prezentare generală</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={handleExportData}
                        variant="outline"
                        className="flex items-center gap-2"
                        title="Export date (backup)"
                    >
                        <Download size={18} />
                        <span className="hidden sm:inline">Export</span>
                    </Button>
                    <Button
                        onClick={handleImportClick}
                        variant="outline"
                        className="flex items-center gap-2"
                        disabled={importing}
                        title="Import date din backup"
                    >
                        <Upload size={18} />
                        <span className="hidden sm:inline">{importing ? 'Se importă...' : 'Import'}</span>
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".json"
                        className="hidden"
                    />
                    <Button
                        onClick={() => setShowSettings(true)}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <Settings size={18} />
                        <span className="hidden sm:inline">Setări</span>
                    </Button>
                </div>
            </div>

            {/* Active Pallet Banner */}
            <div className={`rounded-lg p-4 mb-6 border ${activePallet ? 'bg-amber-50 border-amber-200' : 'bg-zinc-100 border-zinc-200'}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${activePallet ? 'bg-amber-100' : 'bg-zinc-200'}`}>
                            <PackageOpen size={24} className={activePallet ? 'text-amber-600' : 'text-zinc-500'} />
                        </div>
                        <div>
                            {activePallet ? (
                                <>
                                    <p className="font-bold text-amber-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        Palet {activePallet.number} din {palletDate}
                                    </p>
                                    <p className="text-sm text-amber-600">Paletul activ - produsele noi intră automat aici</p>
                                </>
                            ) : (
                                <>
                                    <p className="font-bold text-zinc-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        Niciun palet deschis
                                    </p>
                                    <p className="text-sm text-zinc-500">Deschide un palet pentru a începe</p>
                                </>
                            )}
                        </div>
                    </div>
                    <Button
                        onClick={() => setShowPalletModal(true)}
                        variant="outline"
                        className={activePallet ? 'border-amber-300 text-amber-700 hover:bg-amber-100' : ''}
                    >
                        <PackageOpen size={18} className="mr-2" />
                        {activePallet ? 'Palet Nou' : 'Deschide Palet'}
                    </Button>
                </div>
            </div>
            
            {/* Quick Action - Încărcare Marfă */}
            {activePallet && (
                <div className="mb-6">
                    <Button
                        onClick={() => setShowLoadingModal(true)}
                        size="lg"
                        className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 h-14 text-base"
                    >
                        <Truck size={22} className="mr-3" />
                        Încărcare Marfă (Depozit / Magazin)
                    </Button>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                    <AlertCircle className="text-red-500" size={20} />
                    <span className="text-red-700">{error}</span>
                </div>
            )}

            {/* Stats Grid - Depozit & Magazin */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                {loading ? (
                    <>
                        {[1, 2].map((i) => (
                            <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                                <Skeleton className="h-4 w-16 mb-3" />
                                <Skeleton className="h-8 w-12 mb-1" />
                                <Skeleton className="h-3 w-14" />
                            </div>
                        ))}
                    </>
                ) : (
                    statCards.map((card) => (
                        <div
                            key={card.label}
                            data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`${card.bg} border border-zinc-200 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow`}
                            onClick={() => navigate(card.link)}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <card.icon size={16} className={card.color} />
                                <span className="text-xs font-medium text-zinc-600">
                                    {card.label}
                                </span>
                            </div>
                            <div className={`text-2xl font-bold ${card.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {card.valueLabel}
                            </div>
                            <p className="text-sm text-zinc-500">{card.value} buc</p>
                        </div>
                    ))
                )}
            </div>

            {/* Centered Orders Card with Customer Stats */}
            {!loading && stats && (
                <div 
                    className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-6 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate('/customers')}
                    data-testid="orders-summary-card"
                >
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <BookmarkCheck size={20} className="text-violet-600" />
                        <span className="text-sm font-semibold text-violet-700">Comenzi</span>
                    </div>
                    
                    {/* Main value - centered */}
                    <div className="text-center mb-4">
                        <div className="text-4xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {formatPrice(stats.reserved_value)} <span className="text-lg">RON</span>
                        </div>
                        <p className="text-sm text-violet-500">{stats.total_reserved_quantity} produse în comenzi</p>
                    </div>
                    
                    {/* Customer stats - grid at bottom */}
                    <div className="grid grid-cols-2 gap-3 border-t border-violet-200 pt-3">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-zinc-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {stats.total_customers || 0}
                            </div>
                            <p className="text-xs text-zinc-500">Clienți Total</p>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {stats.active_customers || 0}
                            </div>
                            <p className="text-xs text-zinc-500">Clienți Activi</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Profit Section */}
            {profitStats && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    {/* Estimated Profit Card */}
                    <div className={`rounded-xl p-5 text-white shadow-lg ${
                        profitStats.estimated_profit >= 0 
                            ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' 
                            : 'bg-gradient-to-br from-red-500 to-red-600'
                    }`}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold opacity-90">Profit Estimat</h3>
                            <div className="p-2 bg-white/20 rounded-lg">
                                {profitStats.estimated_profit >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                            </div>
                        </div>
                        <div className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {formatPrice(profitStats.estimated_profit)} <span className="text-xl">RON</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                            <div className="bg-white/10 rounded-lg p-2">
                                <div className="flex items-center gap-1 opacity-75 mb-1">
                                    <ShoppingCart size={12} />
                                    <span>Vânzări</span>
                                </div>
                                <div className="font-semibold">{formatPrice(profitStats.sales_total)}</div>
                            </div>
                            <div className="bg-white/10 rounded-lg p-2">
                                <div className="flex items-center gap-1 opacity-75 mb-1">
                                    <BookmarkCheck size={12} />
                                    <span>Comenzi</span>
                                </div>
                                <div className="font-semibold">{formatPrice(profitStats.orders_total)}</div>
                            </div>
                            <div className="bg-white/10 rounded-lg p-2">
                                <div className="flex items-center gap-1 opacity-75 mb-1">
                                    <Receipt size={12} />
                                    <span>Cheltuieli</span>
                                </div>
                                <div className="font-semibold">-{formatPrice(profitStats.expenses_total)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Monthly Chart */}
                    <div className="bg-white border border-zinc-200 rounded-xl p-5">
                        <h3 className="text-lg font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Evoluție Lunară
                        </h3>
                        <div className="h-48">
                            {profitStats.chart_data && profitStats.chart_data.length > 0 ? (
                                <div className="flex items-end justify-between h-full gap-2">
                                    {profitStats.chart_data.map((month, idx) => {
                                        const maxVal = Math.max(...profitStats.chart_data.map(m => Math.max(m.sales, m.expenses, Math.abs(m.profit)))) || 1;
                                        const salesHeight = (month.sales / maxVal) * 100;
                                        const expensesHeight = (month.expenses / maxVal) * 100;
                                        const isProfit = month.profit >= 0;
                                        
                                        return (
                                            <div key={idx} className="flex-1 flex flex-col items-center">
                                                <div className="w-full flex gap-1 items-end h-36">
                                                    {/* Sales bar */}
                                                    <div 
                                                        className="flex-1 bg-emerald-400 rounded-t transition-all hover:bg-emerald-500"
                                                        style={{ height: `${Math.max(salesHeight, 2)}%` }}
                                                        title={`Vânzări: ${formatPrice(month.sales)} RON`}
                                                    />
                                                    {/* Expenses bar */}
                                                    <div 
                                                        className="flex-1 bg-red-400 rounded-t transition-all hover:bg-red-500"
                                                        style={{ height: `${Math.max(expensesHeight, 2)}%` }}
                                                        title={`Cheltuieli: ${formatPrice(month.expenses)} RON`}
                                                    />
                                                </div>
                                                {/* Profit indicator */}
                                                <div className={`text-xs font-semibold mt-1 ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                                                    {isProfit ? '+' : ''}{formatPrice(month.profit)}
                                                </div>
                                                <span className="text-xs text-zinc-500 mt-1">{month.month}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-full text-zinc-400">
                                    Nu există date suficiente
                                </div>
                            )}
                        </div>
                        {/* Legend */}
                        <div className="flex items-center justify-center gap-6 mt-3 text-xs">
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-emerald-400 rounded" />
                                <span className="text-zinc-600">Vânzări</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-red-400 rounded" />
                                <span className="text-zinc-600">Cheltuieli</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Actual Profit Summary */}
            {profitStats && (
                <div className="bg-zinc-100 border border-zinc-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${profitStats.actual_profit >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                                {profitStats.actual_profit >= 0 ? (
                                    <TrendingUp size={20} className="text-emerald-600" />
                                ) : (
                                    <TrendingDown size={20} className="text-red-600" />
                                )}
                            </div>
                            <div>
                                <p className="text-sm text-zinc-500">Profit Real (doar vânzări finalizate)</p>
                                <p className={`text-xl font-bold ${profitStats.actual_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    {formatPrice(profitStats.actual_profit)} RON
                                </p>
                            </div>
                        </div>
                        <div className="text-right text-sm text-zinc-500">
                            <p>Vânzări: {formatPrice(profitStats.sales_total)} RON</p>
                            <p>Cheltuieli: {formatPrice(profitStats.expenses_total)} RON</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Acțiuni Rapide
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <Link
                        to="/products"
                        data-testid="quick-action-add-product"
                        className="flex items-center gap-3 p-4 border border-zinc-200 rounded-lg hover:border-zinc-300 hover:bg-zinc-50 transition-all"
                    >
                        <div className="p-2 bg-zinc-100 rounded-lg">
                            <Package size={20} className="text-zinc-600" />
                        </div>
                        <div>
                            <p className="font-medium text-zinc-900">Produse</p>
                            <p className="text-xs text-zinc-500">Catalog</p>
                        </div>
                    </Link>
                    <Link
                        to="/depozit"
                        data-testid="quick-action-depozit"
                        className="flex items-center gap-3 p-4 border border-amber-200 rounded-lg hover:border-amber-300 hover:bg-amber-50 transition-all"
                    >
                        <div className="p-2 bg-amber-100 rounded-lg">
                            <Warehouse size={20} className="text-amber-600" />
                        </div>
                        <div>
                            <p className="font-medium text-zinc-900">Depozit</p>
                            <p className="text-xs text-zinc-500">P01, P02...</p>
                        </div>
                    </Link>
                    <Link
                        to="/store"
                        data-testid="quick-action-store"
                        className="flex items-center gap-3 p-4 border border-emerald-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-all"
                    >
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <Store size={20} className="text-emerald-600" />
                        </div>
                        <div>
                            <p className="font-medium text-zinc-900">Magazin</p>
                            <p className="text-xs text-zinc-500">Expunere</p>
                        </div>
                    </Link>
                    <Link
                        to="/customers"
                        data-testid="quick-action-reservations"
                        className="flex items-center gap-3 p-4 border border-violet-200 rounded-lg hover:border-violet-300 hover:bg-violet-50 transition-all"
                    >
                        <div className="p-2 bg-violet-100 rounded-lg">
                            <BookmarkCheck size={20} className="text-violet-600" />
                        </div>
                        <div>
                            <p className="font-medium text-zinc-900">Comenzi</p>
                            <p className="text-xs text-zinc-500">TikTok</p>
                        </div>
                    </Link>
                    <Link
                        to="/pallets"
                        data-testid="quick-action-pallets"
                        className="flex items-center gap-3 p-4 border border-orange-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-all"
                    >
                        <div className="p-2 bg-orange-100 rounded-lg">
                            <FileSpreadsheet size={20} className="text-orange-600" />
                        </div>
                        <div>
                            <p className="font-medium text-zinc-900">Paleți Intrare</p>
                            <p className="text-xs text-zinc-500">Rapoarte</p>
                        </div>
                    </Link>
                </div>
            </div>

            {/* Info Banner */}
            <div className="mt-6 bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-lg p-6 text-white">
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-white/10 rounded-lg">
                        <TrendingUp size={24} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Flux de lucru
                        </h3>
                        <p className="text-white/70 text-sm mt-1">
                            1. Deschide un palet → 2. Adaugă produse în Depozit sau Magazin → 3. La adăugare în comenzi, 
                            se mută automat din Magazin/Depozit → 4. La finalizare comandă, se scoate din stock
                        </p>
                    </div>
                </div>
            </div>

            {/* Settings Modal */}
            <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Setări Aplicație
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6 mt-4">
                        {/* Auto-create toggle */}
                        <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                            <div>
                                <p className="font-medium text-zinc-900">Auto-creare produs nou</p>
                                <p className="text-sm text-zinc-500">
                                    Permite crearea automată de produse noi la client când nu există în stock
                                </p>
                            </div>
                            <button
                                onClick={() => setAutoCreateEnabled(!autoCreateEnabled)}
                                className={`p-1 rounded-full transition-colors ${
                                    autoCreateEnabled ? 'text-emerald-500' : 'text-zinc-400'
                                }`}
                            >
                                {autoCreateEnabled ? (
                                    <ToggleRight size={36} />
                                ) : (
                                    <ToggleLeft size={36} />
                                )}
                            </button>
                        </div>
                        
                        {/* Euro rate */}
                        <div>
                            <Label htmlFor="euroRate" className="flex items-center gap-2">
                                <Euro size={16} />
                                Curs Euro (1 EUR = RON)
                            </Label>
                            <Input
                                id="euroRate"
                                type="number"
                                step="0.01"
                                min="0"
                                value={tempEuroRate}
                                onChange={(e) => setTempEuroRate(e.target.value)}
                                className="mt-2"
                            />
                            <p className="text-xs text-zinc-500 mt-1">
                                Se folosește pentru conversia valorilor în Euro în rapoarte.
                            </p>
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowSettings(false)}
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={saveSettings}
                                className="flex-1 bg-zinc-900 hover:bg-zinc-800"
                            >
                                Salvează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* New Pallet Modal */}
            <Dialog open={showPalletModal} onOpenChange={setShowPalletModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Deschide Palet Nou
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {activePallet && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-sm text-amber-700">
                                    Paletul {activePallet.number} va fi închis automat.
                                </p>
                            </div>
                        )}
                        
                        <div>
                            <Label htmlFor="palletNumber">Număr Palet</Label>
                            <Input
                                id="palletNumber"
                                type="number"
                                min="1"
                                value={newPalletNumber}
                                onChange={(e) => setNewPalletNumber(e.target.value)}
                                placeholder="Ex: 1, 2, 3..."
                                className="mt-1"
                                autoFocus
                            />
                            <p className="text-xs text-zinc-500 mt-1">
                                Data deschiderii: {new Date().toLocaleDateString('ro-RO')}
                            </p>
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowPalletModal(false)}
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={openNewPallet}
                                className="flex-1 bg-amber-500 hover:bg-amber-600"
                                disabled={!newPalletNumber}
                            >
                                <PackageOpen size={18} className="mr-2" />
                                Deschide Palet
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Loading Modal */}
            <LoadingModal
                isOpen={showLoadingModal}
                onClose={() => setShowLoadingModal(false)}
                onComplete={loadStats}
            />
        </div>
    );
};

export default Dashboard;
